import { useState, useEffect, useCallback } from "react";

export interface DebugInfo {
  mimeType: string | null;
  sampleRate: number | null;
  blobSize: number | null;
  duration: number | null;
  maxAmplitude: number | null;
}

interface UseDebugModeReturn {
  debugMode: boolean;
  mounted: boolean;
  debugInfo: DebugInfo;
  setDebugInfo: React.Dispatch<React.SetStateAction<DebugInfo>>;
  updateDebugInfo: (info: Partial<DebugInfo>) => void;
  log: (...args: unknown[]) => void; // Conditional logging helper
}

const INITIAL_DEBUG_INFO: DebugInfo = {
  mimeType: null,
  sampleRate: null,
  blobSize: null,
  duration: null,
  maxAmplitude: null,
};

/**
 * Hook to manage debug mode from URL parameters (?debugAudio=1).
 * Provides debug state, debug info storage, and conditional logging helper.
 */
export function useDebugMode(): UseDebugModeReturn {
  const [mounted, setMounted] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo>(INITIAL_DEBUG_INFO);

  // Initialize debug mode after mount (client-side only)
  useEffect(() => {
    setMounted(true);
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const isDebug = params.get("debugAudio") === "1";
      if (isDebug) {
        console.log("[RecorderPanel] Mounted - debug mode check:", {
          search: window.location.search,
          href: window.location.href,
          debugAudio: params.get("debugAudio"),
          isDebug,
        });
      }
      setDebugMode(isDebug);
    }
  }, []);

  // Listen for URL changes (popstate) to update debug mode
  useEffect(() => {
    if (typeof window !== "undefined") {
      const checkDebugMode = () => {
        const params = new URLSearchParams(window.location.search);
        const isDebug = params.get("debugAudio") === "1";
        if (debugMode || isDebug) {
          console.log("[RecorderPanel] Debug mode check:", {
            search: window.location.search,
            href: window.location.href,
            debugAudio: params.get("debugAudio"),
            isDebug,
            currentDebugMode: debugMode,
          });
        }
        setDebugMode(isDebug);
      };

      // Check immediately in case URL changed
      checkDebugMode();

      // Also listen for popstate in case URL changes
      window.addEventListener("popstate", checkDebugMode);
      return () => window.removeEventListener("popstate", checkDebugMode);
    }
  }, [debugMode]);

  // Helper to update debug info (partial updates)
  const updateDebugInfo = useCallback((info: Partial<DebugInfo>) => {
    setDebugInfo((prev) => ({ ...prev, ...info }));
  }, []);

  // Conditional logging helper
  const log = useCallback(
    (...args: unknown[]) => {
      if (debugMode) {
        console.log(...args);
      }
    },
    [debugMode]
  );

  return {
    debugMode,
    mounted,
    debugInfo,
    setDebugInfo,
    updateDebugInfo,
    log,
  };
}
